import React from 'react';
import { Grid, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import SubmissionPublicationDataTable from './components/submission-publication-data-table';
import SubmissionPublicationDetail from './components/submission-publication-detail';
import { SUBMISSION_TYPE_OPTIONS } from './components/submission-publication-status';
import DefaultSelectClient from '../../../components/default-select-client';
import PageHeader from '../../../components/page-header';
import { FilterPanel } from '../../../components/filter-panel';
import { GridPanel } from '../../../components/list-grid-panel';
import { DateField } from '../../../components/form-page-parts';
import { useSubmissionPublicationSearchController } from './context/submission-publication-search-controller';
import { useSubmissionPublicationSearchState } from './context/submission-publication-search-context';

// Seleção múltipla vazia significa "todos"
function renderMultipleValue(options: { value: number; label: string }[]) {
    return (selected: unknown) => {
        const values = selected as number[];
        if (!values || values.length === 0) return <em>Todos</em>;
        return options.filter((option) => values.includes(option.value)).map((option) => option.label).join(', ');
    };
}

export function SubmissionPublicationSearchComponent() {
    const ctrl = useSubmissionPublicationSearchController();
    const { users, status, parameters } = useSubmissionPublicationSearchState();
    const statusOptions = status.map((item) => ({ value: item.id, label: item.description }));

    const searchOnEnter = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') ctrl.handleSearch();
    };

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Remessas"
                    subtitle="Gerencie as remessas enviadas para publicação no Diário Oficial"
                    breadcrumbs={[{ label: 'Remessas' }]}
                />
            </Grid>

            <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <FilterPanel onClear={ctrl.handleClearFilters} onSearch={ctrl.handleSearch}>
                        <Grid item xs={12} sm={4} md={2}>
                            <InputLabel>Nº remessa</InputLabel>
                            <OutlinedInput type="number" inputProps={{ min: 0 }} name="input-id" value={parameters.id ?? ''} onChange={ctrl.handleOnChange} onKeyDown={searchOnEnter} fullWidth />
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <InputLabel>Status</InputLabel>
                            <Select name="select-status" fullWidth multiple displayEmpty value={parameters.statusId ?? []} onChange={ctrl.handleOnChange} renderValue={renderMultipleValue(statusOptions)}>
                                {statusOptions.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                            </Select>
                        </Grid>
                        <Grid item xs={12} sm={4} md={3}>
                            <InputLabel>Tipo de remessa</InputLabel>
                            <Select name="select-type" fullWidth multiple displayEmpty value={parameters.types ?? []} onChange={ctrl.handleOnChange} renderValue={renderMultipleValue(SUBMISSION_TYPE_OPTIONS)}>
                                {SUBMISSION_TYPE_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                            </Select>
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <DateField label="Enviada a partir de" value={parameters.startDate} maxDate={dayjs()} onChange={ctrl.handleStartDateChange} />
                        </Grid>
                        <Grid item xs={12} sm={6} md={2}>
                            <DateField label="Enviada até" value={parameters.endDate} maxDate={dayjs()} onChange={ctrl.handleEndDateChange} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <InputLabel>Enviada por</InputLabel>
                            <Select name="select-user-id" fullWidth displayEmpty value={parameters.userSenderId ?? ''} onChange={ctrl.handleOnChange}>
                                <MenuItem value=""><em>Todos</em></MenuItem>
                                {users.map((user) => <MenuItem key={user.id} value={user.id}>{user.name}</MenuItem>)}
                            </Select>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <DefaultSelectClient required={false} handleOnChange={ctrl.handleOnChange} clientId={parameters.clientId} />
                        </Grid>
                    </FilterPanel>
                </LocalizationProvider>
            </Grid>

            <Grid item xs={12}>
                <GridPanel>
                    <SubmissionPublicationDataTable
                        rows={ctrl.list.rows}
                        rowCount={ctrl.list.rowCount}
                        loading={ctrl.list.loading}
                        page={parameters.page ?? 0}
                        pageSize={parameters.pageSize ?? 25}
                        onPaginationChange={ctrl.handlePaginationChange}
                        onDetail={ctrl.openDetail}
                    />
                </GridPanel>
            </Grid>

            <SubmissionPublicationDetail
                submissionId={ctrl.selectedSubmissionId}
                status={status}
                onClose={ctrl.closeDetail}
                onSaved={ctrl.list.reload}
            />
        </React.Fragment>
    );
}
